import React, { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { BUSINESS, whatsappLink } from "@/lib/business";
import SocialLinks from "@/components/SocialLinks";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/85 backdrop-blur-md border-b border-[#27272A]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={BUSINESS.logoUrl}
            alt="The1Autospray logo"
            className="w-14 h-14 object-contain drop-shadow-[0_0_12px_rgba(230,0,0,0.35)] group-hover:drop-shadow-[0_0_16px_rgba(230,0,0,0.6)] transition-all"
          />
          <div className="hidden sm:block leading-none">
            <div className="font-display text-lg text-white tracking-wide">
              THE<span className="text-[#E60000]">1</span>AUTOSPRAY
            </div>
            <div className="text-[10px] tracking-[0.3em] text-[#FFCC00] font-bold mt-1">
              PAINT • BODY • PRECISION
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 font-display text-sm tracking-widest transition-all duration-200 ${
                  isActive
                    ? "text-white border-b-2 border-[#E60000]"
                    : "text-[#A1A1AA] hover:text-white border-b-2 border-transparent hover:border-[#FFCC00]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* WhatsApp CTA */}
        <div className="hidden md:flex items-center gap-3">
          <SocialLinks variant="sm" testIdPrefix="header-social" className="hidden xl:flex" />
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-cta"
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-display tracking-widest text-sm px-5 py-3 transition-all duration-300 hover:-translate-y-0.5 pulse-red"
          >
            <MessageCircle className="w-4 h-4" />
            <span>WhatsApp</span>
            <span className="hidden 2xl:inline text-white/80">
              {BUSINESS.whatsapp}
            </span>
          </a>
        </div>

        {/* Mobile menu button */}
        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="lg:hidden bg-black/95 backdrop-blur-md border-t border-[#27272A]"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 font-display text-base tracking-widest transition-colors ${
                    isActive
                      ? "text-[#E60000]"
                      : "text-white hover:text-[#FFCC00]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-whatsapp-cta"
              className="mt-3 inline-flex items-center justify-center gap-2 bg-[#E60000] text-white font-display tracking-widest text-sm px-5 py-3"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp {BUSINESS.whatsapp}
            </a>
            <div className="mt-4 pt-4 border-t border-[#27272A]">
              <div className="font-display text-[10px] tracking-[0.4em] text-[#FFCC00] mb-3">
                FOLLOW US
              </div>
              <SocialLinks variant="default" testIdPrefix="mobile-social" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
